/** @module @liquidefy/code-editor/circuits/terminal-session @version 1.0.0 @license LQD Research LLC */

/**
 * Terminal Session Circuit — Tier 1
 *
 * Creates a fully themed terminal instance with PTY, session, theme,
 * typography, command hints, and preview in one call.
 *
 * Composes: terminal/terminal.ts, terminal/core/pty.ts, terminal/core/session.ts,
 *           terminal/appearance/theme.ts, terminal/appearance/typography.ts,
 *           terminal/guided/hints.ts, terminal/guided/preview.ts
 *
 * @rust terminal_session.rs — false; 4/2/26; sm
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuit-types.js';

import type {
  TerminalState,
  ShellType,
  PTYState,
  EditorResult,
  CommandHistoryEntry,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTerminalId,
} from '../code-editor.js';

import { createTerminal } from '../terminal/terminal.js';
import { createPTY, configurePTY, resizePTY } from '../terminal/core/pty.js';
import { createSession, addCommand, searchHistory } from '../terminal/core/session.js';
import {
  createTerminalTheme,
  terminalThemeToCSS,
} from '../terminal/appearance/theme.js';
import type { TerminalTheme, TerminalColorScheme } from '../terminal/appearance/theme.js';
import {
  createTerminalTypography,
  typographyToCSS,
} from '../terminal/appearance/typography.js';
import type { TerminalTypography } from '../terminal/appearance/typography.js';
import {
  createHintState,
  generatePreExecutionHint,
} from '../terminal/guided/hints.js';
import type { CommandHint, HintState } from '../terminal/guided/hints.js';
import {
  generatePreview,
  createPreviewState,
} from '../terminal/guided/preview.js';
import type { CommandPreview, PreviewState } from '../terminal/guided/preview.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface TerminalSessionConfig {
  readonly name?: string;
  readonly shell?: ShellType;
  readonly colorScheme?: 'dark' | 'light';
  readonly fontSize?: number;
}

export interface TerminalSessionHandle extends CircuitHandle {
  getState(): TerminalState;
  getTheme(): TerminalTheme;
  getTypography(): TerminalTypography;
  resize(cols: number, rows: number): EditorResult<PTYState>;
  switchColorScheme(scheme: 'dark' | 'light'): void;
  addCommand(command: string): void;
  searchHistory(query: string): CommandHistoryEntry[];
  getHint(command: string): CommandHint | null;
  getPreview(command: string): CommandPreview;
  getTerminalCSS(): Record<string, string>;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

export function createTerminalSession(config: TerminalSessionConfig = {}): TerminalSessionHandle {
  // 1. Extract config with defaults
  const name = config.name ?? 'terminal-1';
  const shell: ShellType = config.shell ?? 'zsh';
  let colorScheme: TerminalColorScheme = config.colorScheme ?? 'dark';
  const fontSize = config.fontSize ?? 14;

  // 2. Initialize internal state
  let stats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  // 3. Create terminal
  const termResult = createTerminal({ shell });
  if (!termResult.success) {
    throw new Error(`TerminalSession: failed to create terminal — ${termResult.message}`);
  }
  let terminalState: TerminalState = termResult.value;

  // 4. Create PTY
  const ptyConfig = configurePTY(shell);
  const ptyResult = createPTY(generateId('pty'), ptyConfig);
  if (!ptyResult.success) {
    throw new Error(`TerminalSession: failed to create PTY — ${ptyResult.message}`);
  }
  let ptyState: PTYState = ptyResult.value;

  // 5. Create session
  const sessionResult = createSession(generateId('sess'), shell);
  if (!sessionResult.success) {
    throw new Error(`TerminalSession: failed to create session — ${sessionResult.message}`);
  }
  let sessionState = sessionResult.value;

  // 6. Create theme and typography
  let theme: TerminalTheme = createTerminalTheme(colorScheme);
  let typography: TerminalTypography = createTerminalTypography(fontSize);

  // 7. Hints and previews
  let hintState: HintState = createHintState(true);
  let previewState: PreviewState = createPreviewState(true);

  // 8. Guard
  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §4 DOMAIN METHODS
  // ═════════════════════════════════════════════════════════════════════════════

  function doResize(cols: number, rows: number): EditorResult<PTYState> {
    guardDestroyed();
    const result = resizePTY(ptyState, cols, rows);
    if (result.success) {
      ptyState = result.value;
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doSwitchColorScheme(scheme: 'dark' | 'light'): void {
    guardDestroyed();
    colorScheme = scheme;
    theme = createTerminalTheme(scheme);
    stats = recordSuccess(stats);
  }

  function doAddCommand(command: string): void {
    guardDestroyed();
    sessionState = addCommand(sessionState, command, sessionState.cwd ?? '');
    stats = recordSuccess(stats);
  }

  function doSearchHistory(query: string): CommandHistoryEntry[] {
    guardDestroyed();
    const result = searchHistory(sessionState.history, query);
    stats = recordSuccess(stats);
    return result;
  }

  function doGetHint(command: string): CommandHint | null {
    guardDestroyed();
    stats = recordSuccess(stats);
    return generatePreExecutionHint(command);
  }

  function doGetPreview(command: string): CommandPreview {
    guardDestroyed();
    stats = recordSuccess(stats);
    return generatePreview(command);
  }

  function doGetTerminalCSS(): Record<string, string> {
    guardDestroyed();
    const themeCSS = terminalThemeToCSS(theme);
    const typoCSS = typographyToCSS(typography);
    stats = recordSuccess(stats);
    return { ...themeCSS, ...typoCSS };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §5 FROZEN HANDLE
  // ═════════════════════════════════════════════════════════════════════════════

  return Object.freeze({
    name: 'terminal-session',

    getState(): TerminalState {
      guardDestroyed();
      return terminalState;
    },

    getTheme(): TerminalTheme {
      guardDestroyed();
      return theme;
    },

    getTypography(): TerminalTypography {
      guardDestroyed();
      return typography;
    },

    resize: doResize,
    switchColorScheme: doSwitchColorScheme,
    addCommand: doAddCommand,
    searchHistory: doSearchHistory,
    getHint: doGetHint,
    getPreview: doGetPreview,
    getTerminalCSS: doGetTerminalCSS,

    stats(): CircuitStats {
      return { ...stats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('terminal-session', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('terminal-session', healthFromStats(stats), formatStatsDetails(stats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
